import type {
  MembershipOption,
  MembershipOptionsV2,
  MembershipPricingOption,
  Prices,
  PriceWithMembershipOptions,
  PricingOption,
  PricingToggle,
  Maybe,
  RelatedVariant
} from "@shared/graphql.types";

import React, { useCallback, useState, forwardRef } from "react";
import { ProductATB, OutOfStock } from "@growth/page.products";
import { AccordionWithSingleMembershipOption } from "basket_and_checkout/shared/components/PDP/Pricing/AccordionWithSingleMembershipOption";
import { MembershipOptionsModal } from "packages/core-shopping/components/PDP/MembershipOptionsModal/MembershipOptionsModal";
import * as styles from "./PricingOptions.css";
import { useUserV2 } from "hooks/useUserV2";
import { dispatch } from "@shared/utils.events";
import { RelatedVariants } from "../RelatedVariants/RelatedVariants";
import { useCurrencySymbol } from "hooks";

type Props = {
  membershipOptions: MembershipOptionsV2[];
  pricingOptions: PricingToggle;
  variant: { sku: string; prices: Prices };
  isInWishlist: boolean;
  handleWishlist: () => void;
  onAddToBag: ({
    membershipSKU,
    locationTypeDetails
  }: {
    membershipSKU: string;
    locationTypeDetails: string;
  }) => void;
  context?: string;
  relatedVariants?: Maybe<RelatedVariant[]>;
  selectedVariantSlug?: string;
  isOutOfStock?: boolean;
  handleSubmit: ({ email }: { email: string }) => Promise<boolean>;
  submitted: boolean;
  membershipInBag: boolean;
  isMember: boolean;
  isFreeTrialVariant: boolean;
  onPriceChange?: ((price: PricingOption) => void) | null;
  isStrikethroughVariant?: boolean;
};

const getDefaultMembershipOption = (
  price?: PricingOption
): MembershipOption | undefined => {
  if (!price) return;
  if ("memberships" in price) {
    return (price as PriceWithMembershipOptions).memberships.find(
      (membership) => membership.isDefault
    );
  }
  if ("membership" in price) {
    return (price as MembershipPricingOption).membership;
  }
};
const trackingDetailsMap = {
  1: "Guest Price",
  2: "Members Price"
};

export const PricingOptions = forwardRef<HTMLDivElement, Props>(
  (
    {
      membershipOptions,
      pricingOptions,
      variant,
      onAddToBag,
      handleWishlist,
      isInWishlist,
      context,
      relatedVariants = [],
      selectedVariantSlug,
      isOutOfStock = false,
      handleSubmit,
      submitted,
      membershipInBag,
      isMember,
      onPriceChange = null,
      isStrikethroughVariant = false
    },
    ref
  ) => {
    const { hasGiftMembershipInBag } = useUserV2();
    const prices = pricingOptions.data.prices;
    const productPrices = pricingOptions.data.productPrices;
    const currencySymbol = useCurrencySymbol();

    const defaultMembershipSku =
      membershipOptions?.find((membershipOption) => membershipOption.isDefault)
        ?.sku ?? membershipOptions?.[0]?.sku;

    const defaultPrice = prices.find(
      (priceOption) => priceOption.id === pricingOptions.data.defaultPrice
    );

    const [selectedPrice, setSelectedPrice] = useState(defaultPrice);
    const defaultMembershipForSelectedPrice = getDefaultMembershipOption(
      selectedPrice?.price
    );

    const [selectedMembershipSKU, setSelectedMembershipSKU] = useState<
      string | undefined
    >(defaultMembershipForSelectedPrice?.sku);

    const [isModalOpen, setIsModalOpen] = useState<string | null>(null);

    const onSelectMembership = async (
      chosenMembership: MembershipOptionsV2
    ) => {
      await onAddToBag?.({
        membershipSKU: chosenMembership.sku,
        locationTypeDetails: "membership_selection_panel"
      });
      setIsModalOpen(null);
    };

    const onAccordionChange = useCallback(
      (value: string) => {
        dispatch("page element clicked", {
          target: "PDP - Pricing Options",
          details: `${trackingDetailsMap[value]} selected`,
          location: "PDP - Core Product Info"
        });
        const option = prices.find((priceOption) => priceOption.id === value);
        setSelectedPrice(option);
        if (option && onPriceChange) {
          onPriceChange(option.price);
        }

        if (option) {
          setSelectedMembershipSKU(
            getDefaultMembershipOption(option.price)?.sku
          );
        }
      },
      [prices]
    );

    const showMembershipOptions =
      !isMember && !membershipInBag && !hasGiftMembershipInBag;

    const safeRelatedVariants = relatedVariants ?? [];
    const hasMultipleVariants = safeRelatedVariants.length > 1;

    return (
      <section data-testid="og_pdp_panel">
        {isOutOfStock ? (
          <div className={styles.relatedVariantContainer}>
            <hr className={styles.oosLineBreak} />

            {hasMultipleVariants && (
              <>
                <RelatedVariants
                  relatedVariants={relatedVariants ?? []}
                  selectedVariantSlug={selectedVariantSlug || ""}
                  title="Shade Options"
                />
              </>
            )}

            <OutOfStock
              ref={ref}
              handleSubmit={handleSubmit}
              submitted={submitted}
            />
          </div>
        ) : (
          <>
            {showMembershipOptions && (
              <AccordionWithSingleMembershipOption
                {...{
                  selectedValue: selectedPrice?.id,
                  onAccordionChange,
                  prices: pricingOptions.data.prices || [],
                  productPrices: productPrices ?? undefined,
                  defaultMembershipSku,
                  setShowMembershipOptions: setIsModalOpen,
                  context,
                  isStrikethroughVariant
                }}
              />
            )}
            <div className={styles.relatedVariantContainer}>
              {hasMultipleVariants && (
                <div>
                  <hr className={styles.inStockLineBreak} />
                  <RelatedVariants
                    relatedVariants={relatedVariants ?? []}
                    selectedVariantSlug={selectedVariantSlug || ""}
                    title="Shade Options"
                  />
                </div>
              )}

              <div className={styles.productATBWrapper}>
                <ProductATB
                  isInWishlist={isInWishlist}
                  prices={variant.prices}
                  // NOTE: not currently on ProductATB type and not sure what the use of adding this was, need Charles' input
                  // variantSku={variant.sku}
                  customPrice={
                    showMembershipOptions
                      ? selectedPrice?.price.priceText
                      : undefined
                  }
                  handleWishlist={handleWishlist}
                  handleAddToBag={() =>
                    onAddToBag({
                      membershipSKU: selectedMembershipSKU || "",
                      locationTypeDetails: "pdp-header"
                    })
                  }
                  context="desktop"
                  ref={ref}
                  isMember={isMember}
                  membershipInBag={membershipInBag}
                  currencySymbol={currencySymbol}
                />
              </div>
            </div>
          </>
        )}

        {selectedMembershipSKU && (
          <MembershipOptionsModal
            isOpen={!!isModalOpen}
            onClose={() => setIsModalOpen(null)}
            onSelect={onSelectMembership}
            initialMembershipSku={selectedMembershipSKU}
            memberships={membershipOptions}
            defaultMembershipSku={defaultMembershipSku}
          />
        )}
      </section>
    );
  }
);
